// src/onboarding/onboardingApi.js
//
// The first-login personalization routes (v191; ONE form since v196). Contract:
// <BACKEND_FOLDER>/docs/frontend/first-login-form.md — verified against `dev` source @ 2ad8bdf on 2026-10-02.
// v196: the two old form-1 routes (questions + submission) are gone backend-side, and so are their wrappers.
//
// ⛔ Every call names its workspace: `?tenant=` + suppressTenantHeader. Without it the backend
// answers for the server's DEFAULT workspace, not the user's.
// ⛔ Errors are built with buildApiError (the envelope is FLAT): callers branch on `err.code` /
// `err.status` — never on the server's own message text, which is English.
import { apiFetch } from '../auth/apiFetch.js';
import { buildApiError } from '../context/parseApiError.js';

const BRAND_IDENTITY = '/api/v1/brand-identity';
const BRANDING = '/api/v1/context/branding';

function withTenant(path, tenant, params = {}) {
  if (!tenant) throw new TypeError('onboardingApi: a tenant id is required');
  const qs = new URLSearchParams({ tenant });
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
  });
  return `${path}?${qs.toString()}`;
}

async function fail(res) {
  const body = await res.json().catch(() => ({}));
  const envelope = body?.detail && typeof body.detail === 'object' ? body.detail : body;
  return buildApiError(envelope, res.status);
}

async function call(url, init = {}) {
  const res = await apiFetch(url, { ...init, suppressTenantHeader: true });
  if (!res.ok) throw await fail(res);
  return res.json();
}

/** The status read: the recorded sector (`form.sector`) + the run's state. Never 500s; `degraded: true` keeps the shape. */
export function getOnboardingStatus({ tenant }) {
  return call(withTenant(`${BRAND_IDENTITY}/onboarding`, tenant));
}

/**
 * Logo / favicon. multipart, field `file`. A new upload REPLACES the slot.
 * ⚠️ PARKED: its only caller is ui/BrandingUpload.jsx, which nothing mounts (kept for a future
 * workspace-settings area).
 */
export function uploadBranding({ tenant, slot, file }) {
  const form = new FormData();
  form.append('file', file);
  return call(withTenant(`${BRANDING}/${encodeURIComponent(slot)}`, tenant), { method: 'PUT', body: form });
}

/**
 * THE form — STARTS the personalization run. 202 {status:'started', tenant_id}.
 * v196 (BE 571ac04): `website`, `sector` and `documents` are all OPTIONAL; an empty one is OMITTED.
 * ⛔ Send nothing else (extra="forbid"). The backend reads the listed documents BEFORE answering, so this
 * call can take several seconds.
 */
export function startPersonalization({ tenant, website, sector, documents }) {
  const body = {};
  const site = typeof website === 'string' ? website.trim() : '';
  if (site) body.website = site;
  const sec = typeof sector === 'string' ? sector.trim() : '';
  if (sec) body.sector = sec;
  if (Array.isArray(documents) && documents.length) body.documents = [...new Set(documents)];
  return call(withTenant(`${BRAND_IDENTITY}/personalization`, tenant), {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
